import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API_BASE, apiFetch, SessionUser, getTabToken, setTabToken } from '../api/base';
import { entraLogin, entraLogout, getEntraToken } from '../auth/entra';

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  entraLogin: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  login: async () => {},
  entraLogin: async () => {},
  logout: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  // On boot: restore THIS TAB's session. A tab that logged in with a
  // password restores its own sessionStorage token first (tab-scoped);
  // otherwise fall back to Entra, then to the shared cookie.
  useEffect(() => {
    let active = true;
    if (getTabToken()) {
      apiFetch(`${API_BASE}/auth/me`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (active) {
            setUser(data?.user ?? null);
            setLoading(false);
          }
        })
        .catch(() => {
          if (active) {
            setTabToken(null);
            setLoading(false);
          }
        });
      const onUnauthorizedBoot = () => setTabToken(null);
      window.addEventListener('meridian:unauthorized', onUnauthorizedBoot);
      return () => {
        active = false;
        window.removeEventListener('meridian:unauthorized', onUnauthorizedBoot);
      };
    }
    getEntraToken()
      .then((token) => {
        if (!token) return null;
        return apiFetch(`${API_BASE}/auth/entra/me`)
          .then((res) => (res.ok ? res.json() : null));
      })
      .then((data) => {
        if (data?.user) return data;
        // No Entra session: fall back to the cookie session
        return apiFetch(`${API_BASE}/auth/me`).then((res) => (res.ok ? res.json() : null));
      })
      .then((data) => {
        if (active) {
          setUser(data?.user ?? null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });
    // Session expiry mid-use: any 401 bounces us back to login
    const onUnauthorized = () => setUser(null);
    window.addEventListener('meridian:unauthorized', onUnauthorized);
    return () => {
      active = false;
      window.removeEventListener('meridian:unauthorized', onUnauthorized);
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiFetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || 'Login failed');
    }
    // Keep this login PRIVATE to the current tab so other tabs (logged in
    // as other users) are unaffected. The cookie set by the server remains
    // as the fallback for tabs opened later.
    if (data.sessionToken) setTabToken(data.sessionToken);
    setUser(data.user);
  }, []);

  const entraLoginCb = useCallback(async () => {
    await entraLogin(); // popup; MSAL stores the account
    const res = await apiFetch(`${API_BASE}/auth/entra/me`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Microsoft sign-in failed');
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    // Clear this tab's own token first; only then clear the shared cookie.
    setTabToken(null);
    try {
      await apiFetch(`${API_BASE}/auth/logout`, { method: 'POST' });
    } catch {
      /* clear locally regardless */
    }
    await entraLogout();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, entraLogin: entraLoginCb, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
